import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend } from "chart.js";
import { Activity, BookOpen, Cpu, Download, FileJson, Gauge, Github, GitCompareArrows, KeyRound, LogOut, Plus, RefreshCw, Settings, Terminal } from "lucide-react";
import "./styles.css";
import { useEffect, useMemo, useState, type ReactNode } from "react";

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend);

type Session = {
  user: { id: string; username: string; email: string | null; avatar_url: string | null } | null;
  orgId?: string;
  role?: string;
};

type RunRow = {
  run_id: string;
  project_name: string;
  name: string;
  start_time: number;
  end_time: number | null;
  status: string;
  updated_at: string;
};

type MetricPoint = {
  step: number;
  timestamp?: number;
  metrics: Record<string, number | string | boolean | null>;
};

type SystemPoint = Record<string, number | string | boolean | null>;

type RunData = {
  metrics: MetricPoint[];
  system: SystemPoint[];
  logs: string;
  artifacts: Record<string, unknown>;
};

type TokenRow = {
  id: string;
  name: string;
  token_prefix: string;
  revoked_at: string | null;
  last_used_at: string | null;
  created_at: string;
};

type Route = "dashboard" | "docs" | "settings" | "login";

function currentRoute(): Route {
  const path = window.location.pathname;
  if (path.startsWith("/docs")) return "docs";
  if (path.startsWith("/settings")) return "settings";
  if (path.startsWith("/login")) return "login";
  return "dashboard";
}

function navigate(route: Route) {
  const path = route === "dashboard" ? "/dashboard" : route === "settings" ? "/settings" : `/${route}`;
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

async function fetchJson<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const response = await fetch(input, {
    credentials: "same-origin",
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {})
    }
  });
  const body = await response.json().catch(() => ({})) as { error?: string };
  if (!response.ok) throw new Error(body.error || `Request failed: ${response.status}`);
  return body as T;
}

function downloadText(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function App() {
  const [route, setRoute] = useState<Route>(currentRoute());
  const [session, setSession] = useState<Session>({ user: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const onPop = () => setRoute(currentRoute());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  async function refreshSession() {
    setLoading(true);
    try {
      setSession(await fetchJson<Session>("/api/session"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refreshSession();
  }, []);

  useEffect(() => {
    if (!loading && !session.user && route !== "login") navigate("login");
    if (!loading && session.user && route === "login") navigate("dashboard");
  }, [loading, route, session.user]);

  if (loading) return <main className="login-page"><div className="card card-pad">Loading RunGoblin...</div></main>;
  if (!session.user || route === "login") return <LoginPage />;

  return (
    <AppShell route={route} session={session} refreshSession={refreshSession}>
      {route === "docs" ? <DocsPage /> : null}
      {route === "settings" ? <SettingsPage /> : null}
      {route === "dashboard" ? <DashboardPage /> : null}
    </AppShell>
  );
}

function LoginPage() {
  const error = new URLSearchParams(window.location.search).get("error");
  return (
    <main className="login-page">
      <section className="card card-pad login-card">
        <div className="brand">
          <div className="brand-mark"><Gauge size={17} /></div>
          <span>RunGoblin</span>
        </div>
        <div className="grid">
          <p className="eyebrow">Realtime ML telemetry</p>
          <h1>Sign in to your training dashboard</h1>
          <p className="muted">Use GitHub to access live runs, API tokens, logs, charts, and profiler data.</p>
        </div>
        {error ? <p className="status failed">GitHub sign-in failed. Check the OAuth callback URL.</p> : null}
        <a className="button primary" href="/api/auth/github/start"><Github size={16} />Continue with GitHub</a>
      </section>
    </main>
  );
}

function AppShell({ children, route, session, refreshSession }: { children: ReactNode; route: Route; session: Session; refreshSession: () => Promise<void> }) {
  async function signOut() {
    await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" });
    await refreshSession();
    navigate("login");
  }

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark"><Gauge size={17} /></div>
          <span>RunGoblin</span>
        </div>
        <nav className="nav">
          <button className={route === "dashboard" ? "active" : ""} onClick={() => navigate("dashboard")}><Activity size={16} />Dashboard</button>
          <button className={route === "docs" ? "active" : ""} onClick={() => navigate("docs")}><BookOpen size={16} />Docs</button>
          <button className={route === "settings" ? "active" : ""} onClick={() => navigate("settings")}><Settings size={16} />Settings</button>
          <button onClick={signOut}><LogOut size={16} />Sign out</button>
        </nav>
      </aside>
      <main className="main">
        <header className="topbar">
          <div className="muted">{session.user?.username}</div>
          <button className="button" onClick={() => navigate("settings")}><KeyRound size={16} />API tokens</button>
        </header>
        <div className="content">{children}</div>
      </main>
    </div>
  );
}

function DashboardPage() {
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [activeRunId, setActiveRunId] = useState("");
  const [data, setData] = useState<RunData>({ metrics: [], system: [], logs: "", artifacts: {} });
  const [tab, setTab] = useState<"charts" | "hardware" | "logs" | "profiler" | "compare" | "exports">("charts");
  const [error, setError] = useState("");

  async function loadRuns() {
    try {
      const body = await fetchJson<{ runs: RunRow[] }>("/api/runs");
      setRuns(body.runs);
      setActiveRunId((current) => current || body.runs[0]?.run_id || "");
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load runs.");
    }
  }

  async function loadRunData(runId: string) {
    if (!runId) return;
    try {
      setData(await fetchJson<RunData>(`/api/runs/${encodeURIComponent(runId)}/data`));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load run data.");
    }
  }

  useEffect(() => {
    loadRuns();
    const id = window.setInterval(loadRuns, 3000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    loadRunData(activeRunId);
    if (!activeRunId) return;
    const id = window.setInterval(() => loadRunData(activeRunId), 3000);
    return () => window.clearInterval(id);
  }, [activeRunId]);

  const activeRun = runs.find((run) => run.run_id === activeRunId);
  const profile = data.artifacts.profile as Record<string, unknown> | undefined;
  const lossData = {
    labels: data.metrics.map((point) => point.step),
    datasets: [
      {
        label: "loss",
        data: data.metrics.map((point) => Number(point.metrics?.loss ?? NaN)),
        borderColor: "#2457a6",
        backgroundColor: "#2457a6",
        tension: 0.25,
        pointRadius: 0
      },
      {
        label: "accuracy",
        data: data.metrics.map((point) => Number(point.metrics?.accuracy ?? point.metrics?.acc ?? NaN)),
        borderColor: "#17775b",
        backgroundColor: "#17775b",
        tension: 0.25,
        pointRadius: 0
      }
    ]
  };
  const hardwareData = {
    labels: data.system.map((point) => new Date(Number(point.timestamp || point.ts || 0) * 1000).toLocaleTimeString()),
    datasets: [
      {
        label: "CPU %",
        data: data.system.map((point) => Number(point.cpu_percent ?? NaN)),
        borderColor: "#2457a6",
        backgroundColor: "#2457a6",
        pointRadius: 0
      },
      {
        label: "Memory %",
        data: data.system.map((point) => Number(point.memory_percent ?? NaN)),
        borderColor: "#9a5b13",
        backgroundColor: "#9a5b13",
        pointRadius: 0
      }
    ]
  };
  const exportName = (activeRun?.name || activeRunId || "run").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  return (
    <div className="grid">
      <div className="page-head">
        <div>
          <p className="eyebrow">Live dashboard</p>
          <h1>{activeRun?.name || "Waiting for runs"}</h1>
          <p className="muted">The Python client writes locally first, then streams batched updates to Cloudflare.</p>
        </div>
        <button className="button" onClick={loadRuns}><RefreshCw size={16} />Refresh</button>
      </div>
      {error ? <div className="status failed">{error}</div> : null}

      <section className="grid metrics">
        <div className="card card-pad stat"><span className="muted">Runs</span><span className="stat-value">{runs.length}</span></div>
        <div className="card card-pad stat"><span className="muted">Metric points</span><span className="stat-value">{data.metrics.length}</span></div>
        <div className="card card-pad stat"><span className="muted">System samples</span><span className="stat-value">{data.system.length}</span></div>
        <div className="card card-pad stat"><span className="muted">Status</span><span className={`status ${activeRun?.status}`}>{activeRun?.status || "idle"}</span></div>
      </section>

      <section className="grid workspace">
        <aside className="card run-list">
          {runs.length ? runs.map((run) => (
            <button key={run.run_id} className={`run-row ${run.run_id === activeRunId ? "active" : ""}`} onClick={() => setActiveRunId(run.run_id)}>
              <strong>{run.name}</strong>
              <span className="muted">{run.run_id.slice(0, 8)} - {new Date(run.start_time * 1000).toLocaleString()}</span>
              <span className={`status ${run.status}`}>{run.status}</span>
            </button>
          )) : <div className="card-pad muted">No runs yet. Create an API token, run `goblin login`, then start training.</div>}
        </aside>

        <div className="card card-pad">
          <div className="tabs">
            <button className={`tab ${tab === "charts" ? "active" : ""}`} onClick={() => setTab("charts")}><Activity size={14} /> Charts</button>
            <button className={`tab ${tab === "hardware" ? "active" : ""}`} onClick={() => setTab("hardware")}><Cpu size={14} /> Hardware</button>
            <button className={`tab ${tab === "logs" ? "active" : ""}`} onClick={() => setTab("logs")}><Terminal size={14} /> Logs</button>
            <button className={`tab ${tab === "profiler" ? "active" : ""}`} onClick={() => setTab("profiler")}><FileJson size={14} /> Profiler</button>
            <button className={`tab ${tab === "compare" ? "active" : ""}`} onClick={() => setTab("compare")}><GitCompareArrows size={14} /> Compare</button>
            <button className={`tab ${tab === "exports" ? "active" : ""}`} onClick={() => setTab("exports")}><Download size={14} /> Exports</button>
          </div>
          {tab === "charts" ? <div className="chart-panel"><Line data={lossData} options={{ maintainAspectRatio: false, responsive: true }} /></div> : null}
          {tab === "hardware" ? <div className="chart-panel"><Line data={hardwareData} options={{ maintainAspectRatio: false, responsive: true }} /></div> : null}
          {tab === "logs" ? <pre style={{ minHeight: 360, whiteSpace: "pre-wrap" }}>{data.logs || "No logs captured yet."}</pre> : null}
          {tab === "profiler" ? <pre style={{ minHeight: 360, whiteSpace: "pre-wrap" }}>{profile ? JSON.stringify(profile, null, 2) : "No profiler artifact captured yet."}</pre> : null}
          {tab === "compare" ? (
            <table className="table">
              <thead><tr><th>Run</th><th>Status</th><th>Started</th><th>Duration</th></tr></thead>
              <tbody>{runs.slice(0, 6).map((run) => (
                <tr key={run.run_id}>
                  <td>{run.name}</td>
                  <td><span className={`status ${run.status}`}>{run.status}</span></td>
                  <td>{new Date(run.start_time * 1000).toLocaleString()}</td>
                  <td>{run.end_time ? `${Math.round(run.end_time - run.start_time)}s` : "running"}</td>
                </tr>
              ))}</tbody>
            </table>
          ) : null}
          {tab === "exports" ? (
            <div className="export-grid">
              <button className="button" disabled={!data.metrics.length} onClick={() => downloadText(`${exportName}-metrics.json`, JSON.stringify(data.metrics, null, 2), "application/json")}><Download size={16} />Metrics JSON</button>
              <button className="button" disabled={!data.system.length} onClick={() => downloadText(`${exportName}-system.json`, JSON.stringify(data.system, null, 2), "application/json")}><Download size={16} />System JSON</button>
              <button className="button" disabled={!data.logs} onClick={() => downloadText(`${exportName}-logs.txt`, data.logs, "text/plain")}><Download size={16} />Logs TXT</button>
              <button className="button" disabled={!profile} onClick={() => downloadText(`${exportName}-profile.json`, JSON.stringify(profile, null, 2), "application/json")}><Download size={16} />Profile JSON</button>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}

function SettingsPage() {
  const [tokens, setTokens] = useState<TokenRow[]>([]);
  const [name, setName] = useState("Local training token");
  const [newToken, setNewToken] = useState("");
  const [error, setError] = useState("");

  async function loadTokens() {
    try {
      const body = await fetchJson<{ tokens: TokenRow[] }>("/api/tokens");
      setTokens(body.tokens);
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load tokens.");
    }
  }

  async function createToken() {
    try {
      const body = await fetchJson<{ token: string }>("/api/tokens", { method: "POST", body: JSON.stringify({ name }) });
      setNewToken(body.token);
      await loadTokens();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create token.");
    }
  }

  async function revokeToken(id: string) {
    await fetchJson(`/api/tokens/${encodeURIComponent(id)}`, { method: "DELETE" });
    await loadTokens();
  }

  useEffect(() => {
    loadTokens();
  }, []);

  return (
    <div className="grid">
      <div className="page-head">
        <div>
          <p className="eyebrow">Settings</p>
          <h1>API tokens</h1>
          <p className="muted">Create a token, then run `goblin login --api-url https://your-site.pages.dev --token rg_live_...`.</p>
        </div>
      </div>
      {error ? <div className="status failed">{error}</div> : null}
      <section className="card card-pad form">
        <div className="field">
          <label>Token name</label>
          <input value={name} onChange={(event) => setName(event.target.value)} />
        </div>
        <button className="button primary" onClick={createToken}><Plus size={16} />Create token</button>
        {newToken ? <pre>{newToken}</pre> : null}
      </section>
      <section className="card card-pad">
        <table className="table">
          <thead><tr><th>Name</th><th>Prefix</th><th>Last used</th><th></th></tr></thead>
          <tbody>
            {tokens.map((token) => (
              <tr key={token.id}>
                <td>{token.name}</td>
                <td>{token.token_prefix}</td>
                <td>{token.last_used_at ? new Date(token.last_used_at).toLocaleString() : "never"}</td>
                <td>{token.revoked_at ? "revoked" : <button className="button danger" onClick={() => revokeToken(token.id)}><KeyRound size={15} />Revoke</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function DocsPage() {
  const appUrl = window.location.origin;
  const installSnippet = useMemo(() => `pip install rungoblin`, []);
  const loginSnippet = useMemo(() => `goblin login --api-url ${appUrl} --token rg_live_...\ngoblin where`, [appUrl]);
  return (
    <article className="docs card card-pad">
      <p className="eyebrow">Documentation</p>
      <h1>RunGoblin Python Client</h1>
      <p>
        Install the Python package, generate an API token from Settings, then run training normally.
        Runs are cached locally under <code>~/.rungoblin</code> and streamed to Cloudflare in the background.
      </p>
      <h2>Install</h2>
      <pre><code>{installSnippet}</code></pre>
      <h2>Connect this machine</h2>
      <pre><code>{loginSnippet}</code></pre>
      <h2>Instrument training</h2>
      <pre><code>{`import goblin

goblin.init(project="mnist", name="baseline", config={"lr": 0.001})

for step in range(100):
    goblin.log({"loss": 1.0 / (step + 1), "accuracy": step / 100}, step=step)

goblin.finish()`}</code></pre>
      <h2>Offline and migration</h2>
      <p>
        If the network is unavailable, RunGoblin keeps appending local files and queues cloud events.
        Run <code>goblin sync</code> later to replay queued events and local runs.
      </p>
      <pre><code>{`goblin migrate /path/to/old-project\ngoblin sync`}</code></pre>
    </article>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
